import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiGithub, FiMail, FiLock, FiUser, FiLoader, FiEye, FiEyeOff } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

type Mode = 'login' | 'register';

const ALLOWED_REDIRECTS = new Set(['/submit', '/browse', '/profile', '/evaluate']);

function safeRedirect(target: string | null): string {
  if (!target) return '/';
  const base = target.split('?')[0];
  return ALLOWED_REDIRECTS.has(base) ? target : '/';
}

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const redirect = safeRedirect(params.get('redirect'));

  useEffect(() => {
    if (redirect !== '/') sessionStorage.setItem('postLoginRedirect', redirect);
  }, [redirect]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGithubLogin = () => {
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    window.location.href = `${apiUrl}/auth/github`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (mode === 'register' && !username) {
      toast.error('Please enter a username');
      return;
    }

    // Client-side password validation for registration
    if (mode === 'register') {
      if (password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
      if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
        toast.error('Password must contain uppercase, lowercase, and a number');
        return;
      }
    }

    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const payload = mode === 'login'
        ? { email, password }
        : { email, password, username };

      await api.post(endpoint, payload);
      // Cookie is set by the server — just re-check auth state
      await login();
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created!');
      sessionStorage.removeItem('postLoginRedirect');
      navigate(redirect);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.response?.data?.error || `Failed to ${mode}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen flex items-center justify-center relative">
      {/* Background ambient */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[500px] h-[500px] rounded-full bg-gradient-to-r from-brand-500/5 to-brand-purple-500/5 blur-[120px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        className="relative z-10 w-full max-w-md px-4"
      >
        <div className="glass-card p-8 sm:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <img src="/favicon.svg" alt="Strongly.AI" className="w-14 h-14 rounded-2xl mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-1">
              {mode === 'login' ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-sm text-slate-500">
              {mode === 'login'
                ? 'Sign in to manage your skills'
                : 'Join the AI skills community'}
            </p>
          </div>

          {/* GitHub Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleGithubLogin}
            className="w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl bg-white/[0.08] border border-white/10 text-white font-semibold text-sm hover:bg-white/[0.12] hover:border-white/20 transition-all"
          >
            <FiGithub size={18} />
            Continue with GitHub
          </motion.button>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-slate-500 font-medium">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-xs text-slate-400 mb-1.5 font-medium">Username</label>
                <div className="relative">
                  <FiUser size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="your-username"
                    className="w-full bg-black/50 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-gray-600 outline-none
                               focus:border-brand-500/30 focus:ring-1 focus:ring-brand-500/20 transition-all"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs text-slate-400 mb-1.5 font-medium">Email</label>
              <div className="relative">
                <FiMail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-black/50 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-gray-600 outline-none
                             focus:border-brand-500/30 focus:ring-1 focus:ring-brand-500/20 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-slate-400 mb-1.5 font-medium">Password</label>
              <div className="relative">
                <FiLock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full bg-black/50 border border-white/10 rounded-xl pl-11 pr-12 py-3 text-sm text-white placeholder-gray-600 outline-none
                             focus:border-brand-500/30 focus:ring-1 focus:ring-brand-500/20 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="w-full btn-gradient py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
            >
              <span className="relative z-10 flex items-center gap-2">
                {loading && <FiLoader size={16} className="animate-spin" />}
                {mode === 'login' ? 'Sign In' : 'Create Account'}
              </span>
            </motion.button>
          </form>

          {/* Toggle Mode */}
          <div className="text-center mt-6">
            <span className="text-sm text-slate-500">
              {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
            </span>
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-sm text-brand-400 hover:text-brand-300 ml-1.5 font-medium transition-colors"
            >
              {mode === 'login' ? 'Sign Up' : 'Sign In'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
